import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  FileText,
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Folder,
  FolderOpen,
} from "lucide-react";
import { ArtifactViewerProps, Artifact, WorkspaceEntry } from "../../types";
import { apiService } from "../../services/api";
import { ArtifactDownloadButton } from "./ArtifactDownloadButton";
import { useWebSocketMessages } from "../../hooks/useWebSocket";

type WorkspaceSortKey = "name" | "size" | "created" | "modified";
type SortDirection = "asc" | "desc";

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const getFileExtension = (artifactType: string): string => {
  const map: Record<string, string> = {
    markdown: "md",
    html: "html",
    text: "txt",
    json: "json",
    yaml: "yaml",
  };
  return map[artifactType] || "txt";
};

interface GroupedArtifacts {
  [stepId: string]: Artifact[];
}

// Per-workspace browser state
interface WorkspaceBrowserState {
  expanded: boolean;
  currentPath: string;
  entries: WorkspaceEntry[];
  loading: boolean;
  error: string | null;
  sortKey: WorkspaceSortKey;
  sortDir: SortDirection;
}

export const ArtifactViewer: React.FC<ArtifactViewerProps> = ({
  workflowId,
  workflow,
}) => {
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());

  // Workspace browser state keyed by `${stepId}:${artifactKey}`
  const [workspaceBrowsers, setWorkspaceBrowsers] = useState<
    Map<string, WorkspaceBrowserState>
  >(new Map());

  // Track which workspaces we've already attempted to load
  const loadedWorkspaces = useRef<Set<string>>(new Set());

  // Fetch artifacts function (for initial load and page refresh)
  const fetchArtifacts = useCallback(async () => {
    try {
      // Only show loading spinner on first load
      if (artifacts.length === 0) {
        setLoading(true);
      }
      setError(null);
      const data = await apiService.getWorkflowArtifacts(workflowId);
      setArtifacts(data);
    } catch (err) {
      console.error("Failed to fetch artifacts:", err);
      setError("Failed to load artifacts. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [workflowId, artifacts.length]);

  // Initial fetch on mount
  useEffect(() => {
    fetchArtifacts();
  }, [workflowId, fetchArtifacts]);

  // Listen to WebSocket messages for new artifacts
  useWebSocketMessages(
    "artifact_created",
    useCallback(
      (data: any) => {
        if (data && String(data.workflow_id) === String(workflowId)) {
          // Add or update artifact
          setArtifacts((prev) => {
            const existing = prev.findIndex(
              (a) =>
                a.step_id === data.artifact.step_id &&
                a.artifact_key === data.artifact.artifact_key
            );
            if (existing >= 0) {
              const updated = [...prev];
              updated[existing] = data.artifact;
              return updated;
            }
            return [...prev, data.artifact];
          });
        }
      },
      [workflowId]
    )
  );

  const getWorkspaceBrowserKey = (stepId: string, artifactKey: string) =>
    `${stepId}:${artifactKey}`;

  const getWorkspaceBrowser = (
    stepId: string,
    artifactKey: string
  ): WorkspaceBrowserState => {
    const key = getWorkspaceBrowserKey(stepId, artifactKey);
    return (
      workspaceBrowsers.get(key) || {
        expanded: true, // Show file browser by default
        currentPath: "",
        entries: [],
        loading: false,
        error: null,
        sortKey: "name",
        sortDir: "asc",
      }
    );
  };

  const updateWorkspaceBrowser = useCallback(
    (
      stepId: string,
      artifactKey: string,
      updates: Partial<WorkspaceBrowserState>
    ) => {
      const key = getWorkspaceBrowserKey(stepId, artifactKey);
      setWorkspaceBrowsers((prev) => {
        const newMap = new Map(prev);
        const current = prev.get(key) || {
          expanded: true, // Show file browser by default
          currentPath: "",
          entries: [],
          loading: false,
          error: null,
          sortKey: "name" as WorkspaceSortKey,
          sortDir: "asc" as SortDirection,
        };
        newMap.set(key, { ...current, ...updates });
        return newMap;
      });
    },
    []
  );

  const fetchWorkspaceEntries = useCallback(
    async (stepId: string, artifactKey: string, subpath: string) => {
      updateWorkspaceBrowser(stepId, artifactKey, {
        loading: true,
        error: null,
      });
      try {
        const entries = await apiService.listWorkspaceEntries(
          workflowId,
          subpath
        );
        updateWorkspaceBrowser(stepId, artifactKey, {
          entries,
          loading: false,
          currentPath: subpath,
        });
      } catch (err) {
        console.error("Failed to fetch workspace entries:", err);
        updateWorkspaceBrowser(stepId, artifactKey, {
          error: "Failed to load workspace files",
          loading: false,
          entries: [],
        });
      }
    },
    [workflowId, updateWorkspaceBrowser]
  );

  // Auto-load workspace browser for workspace_path artifacts (only once per workspace)
  useEffect(() => {
    // Only auto-load if the workflow has a workspace_path set
    if (!workflow?.workspace_path) {
      return;
    }

    artifacts.forEach((artifact) => {
      if (artifact.artifact_key === "workspace_path") {
        const key = getWorkspaceBrowserKey(artifact.step_id, artifact.artifact_key);
        
        // Only load if we haven't already attempted to load this workspace
        if (!loadedWorkspaces.current.has(key)) {
          loadedWorkspaces.current.add(key);
          fetchWorkspaceEntries(artifact.step_id, artifact.artifact_key, "");
        }
      }
    });
  }, [artifacts, workflow?.workspace_path, fetchWorkspaceEntries]);

  const handleToggleWorkspaceBrowser = (
    stepId: string,
    artifactKey: string
  ) => {
    const browser = getWorkspaceBrowser(stepId, artifactKey);
    const newExpanded = !browser.expanded;
    updateWorkspaceBrowser(stepId, artifactKey, { expanded: newExpanded });
    if (newExpanded && browser.entries.length === 0) {
      fetchWorkspaceEntries(stepId, artifactKey, "");
    }
  };

  const handleWorkspaceEntryClick = (
    stepId: string,
    artifactKey: string,
    entry: WorkspaceEntry
  ) => {
    if (entry.type === "directory") {
      const browser = getWorkspaceBrowser(stepId, artifactKey);
      const newPath = browser.currentPath
        ? `${browser.currentPath}/${entry.name}`
        : entry.name;
      fetchWorkspaceEntries(stepId, artifactKey, newPath);
    } else {
      // Open file in new window
      handleOpenWorkspaceFile(stepId, artifactKey, entry.name);
    }
  };

  const handleOpenWorkspaceFile = async (
    stepId: string,
    artifactKey: string,
    fileName: string
  ) => {
    try {
      const browser = getWorkspaceBrowser(stepId, artifactKey);
      const relativePath = browser.currentPath
        ? `${browser.currentPath}/${fileName}`
        : fileName;
      const blob = await apiService.getWorkspaceFile(workflowId, relativePath);
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error("Failed to open workspace file:", error);
      alert("Failed to open file. Please try again.");
    }
  };

  const handleWorkspaceBreadcrumbClick = (
    stepId: string,
    artifactKey: string,
    pathSegment: string
  ) => {
    fetchWorkspaceEntries(stepId, artifactKey, pathSegment);
  };

  const toggleWorkspaceSort = (
    stepId: string,
    artifactKey: string,
    key: WorkspaceSortKey
  ) => {
    const browser = getWorkspaceBrowser(stepId, artifactKey);
    updateWorkspaceBrowser(stepId, artifactKey, {
      sortKey: key,
      sortDir: browser.sortDir === "asc" ? "desc" : "asc",
    });
  };

  const getSortedWorkspaceEntries = (
    stepId: string,
    artifactKey: string
  ): WorkspaceEntry[] => {
    const browser = getWorkspaceBrowser(stepId, artifactKey);
    const dirs = browser.entries.filter((e) => e.type === "directory");
    const files = browser.entries.filter((e) => e.type === "file");

    const sort = (a: WorkspaceEntry, b: WorkspaceEntry) => {
      let av: string | number;
      let bv: string | number;
      if (browser.sortKey === "name") {
        av = (a.name ?? "").toLowerCase();
        bv = (b.name ?? "").toLowerCase();
      } else if (browser.sortKey === "size") {
        av = a.size ?? 0;
        bv = b.size ?? 0;
      } else if (
        browser.sortKey === "created" ||
        browser.sortKey === "modified"
      ) {
        av = a[browser.sortKey] ?? "";
        bv = b[browser.sortKey] ?? "";
      } else {
        av = "";
        bv = "";
      }
      if (typeof av === "string" && typeof bv === "string") {
        const cmp = av.localeCompare(bv);
        return browser.sortDir === "asc" ? cmp : -cmp;
      }
      if (typeof av === "number" && typeof bv === "number") {
        const cmp = av - bv;
        return browser.sortDir === "asc" ? cmp : -cmp;
      }
      return 0;
    };

    dirs.sort(sort);
    files.sort(sort);
    return [...dirs, ...files];
  };

  const formatWorkspaceDate = (iso: string) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch {
      return iso;
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Artifacts
        </h2>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-blue-500 animate-spin mr-2" />
          <span className="text-gray-600 dark:text-gray-400">
            Loading artifacts...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Artifacts
        </h2>
        <div className="flex items-center p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
          <span className="text-red-800 dark:text-red-300">{error}</span>
        </div>
      </div>
    );
  }

  if (artifacts.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Artifacts
        </h2>
        <div className="flex flex-col items-center justify-center py-8 text-gray-500 dark:text-gray-400">
          <FileText className="w-12 h-12 mb-2 opacity-50" />
          <span>No artifacts yet</span>
        </div>
      </div>
    );
  }

  // Group artifacts by step_id
  const groupedArtifacts: GroupedArtifacts = artifacts.reduce(
    (acc, artifact) => {
      if (!acc[artifact.step_id]) {
        acc[artifact.step_id] = [];
      }
      acc[artifact.step_id].push(artifact);
      return acc;
    },
    {} as GroupedArtifacts
  );

  const toggleStep = (stepId: string) => {
    setExpandedSteps((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(stepId)) {
        newSet.delete(stepId);
      } else {
        newSet.add(stepId);
      }
      return newSet;
    });
  };

  const handleOpenArtifact = async (stepId: string, artifactKey: string) => {
    try {
      const blob = await apiService.downloadArtifact(
        workflowId,
        stepId,
        artifactKey
      );
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error("Failed to open artifact:", error);
      alert("Failed to open artifact. Please try again.");
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Artifacts ({artifacts.length})
      </h2>
      <div className="space-y-2">
        {Object.entries(groupedArtifacts).map(([stepId, stepArtifacts]) => {
          const isExpanded = expandedSteps.has(stepId);
          const ChevronIcon = isExpanded ? ChevronDown : ChevronRight;

          return (
            <div
              key={stepId}
              className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden"
            >
              {/* Collapsible Header */}
              <button
                onClick={() => toggleStep(stepId)}
                className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-left"
              >
                <div className="flex items-center space-x-2">
                  <ChevronIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  <span className="font-medium text-gray-900 dark:text-white">
                    {stepId}
                  </span>
                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded">
                    {stepArtifacts.length} artifact
                    {stepArtifacts.length !== 1 ? "s" : ""}
                  </span>
                </div>
              </button>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="p-3 bg-white dark:bg-gray-800 space-y-2">
                  {stepArtifacts.map((artifact) => {
                    const extension = getFileExtension(artifact.artifact_type);
                    const fileName = `${artifact.artifact_key}.${extension}`;
                    const fileSize = formatFileSize(
                      artifact.artifact_metadata?.size_bytes || 0
                    );
                    const wordCount = artifact.artifact_metadata?.word_count;
                    const isWorkspacePath =
                      artifact.artifact_key === "workspace_path";

                    return (
                      <div key={artifact.id} className="space-y-0">
                        {/* Artifact Card */}
                        <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <FileText className="w-5 h-5 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <span className="font-medium text-gray-900 dark:text-white truncate">
                                  {artifact.artifact_key}
                                </span>
                                <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 rounded">
                                  {artifact.artifact_type}
                                </span>
                              </div>
                              <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600 dark:text-gray-400">
                                <span>Size: {fileSize}</span>
                                {wordCount && (
                                  <span>Words: {wordCount.toLocaleString()}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="ml-4 flex items-center space-x-2 flex-shrink-0">
                            {/* Workspace Browser Toggle for workspace_path artifacts */}
                            {isWorkspacePath && (
                              <button
                                onClick={() =>
                                  handleToggleWorkspaceBrowser(
                                    artifact.step_id,
                                    artifact.artifact_key
                                  )
                                }
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-300 rounded-md hover:bg-green-100 dark:bg-green-900/20 dark:text-green-300 dark:border-green-600 dark:hover:bg-green-900/30 transition-colors"
                              >
                                {getWorkspaceBrowser(
                                  artifact.step_id,
                                  artifact.artifact_key
                                ).expanded ? (
                                  <>
                                    <FolderOpen className="w-4 h-4 mr-2" />
                                    Hide Files
                                  </>
                                ) : (
                                  <>
                                    <Folder className="w-4 h-4 mr-2" />
                                    Browse Files
                                  </>
                                )}
                              </button>
                            )}
                            {/* Open in New Window */}
                            <button
                              onClick={() =>
                                handleOpenArtifact(
                                  artifact.step_id,
                                  artifact.artifact_key
                                )
                              }
                              className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-300 rounded-md hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-600 dark:hover:bg-blue-900/30 transition-colors"
                            >
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Open
                            </button>
                            {/* Download Button */}
                            <ArtifactDownloadButton
                              workflowId={workflowId}
                              stepId={artifact.step_id}
                              artifactKey={artifact.artifact_key}
                              artifactType={artifact.artifact_type}
                              fileName={fileName}
                            />
                          </div>
                        </div>

                        {/* Inline Workspace Browser */}
                        {isWorkspacePath &&
                          getWorkspaceBrowser(artifact.step_id, artifact.artifact_key)
                            .expanded && (
                            <div className="mt-2 border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
                              <div className="flex items-center justify-between mb-3">
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                  Workspace Files
                                </h3>
                                {/* Breadcrumb */}
                                {getWorkspaceBrowser(
                                  artifact.step_id,
                                  artifact.artifact_key
                                ).currentPath && (
                                  <div className="flex items-center space-x-1 text-sm text-gray-600 dark:text-gray-400">
                                    <button
                                      onClick={() =>
                                        handleWorkspaceBreadcrumbClick(
                                          artifact.step_id,
                                          artifact.artifact_key,
                                          ""
                                        )
                                      }
                                      className="hover:text-blue-600 dark:hover:text-blue-400"
                                    >
                                      root
                                    </button>
                                    {getWorkspaceBrowser(
                                      artifact.step_id,
                                      artifact.artifact_key
                                    )
                                      .currentPath.split("/")
                                      .map((part, idx, arr) => {
                                        const pathTo = arr.slice(0, idx + 1).join("/");
                                        return (
                                          <React.Fragment key={idx}>
                                            <span>/</span>
                                            <button
                                              onClick={() =>
                                                handleWorkspaceBreadcrumbClick(
                                                  artifact.step_id,
                                                  artifact.artifact_key,
                                                  pathTo
                                                )
                                              }
                                              className="hover:text-blue-600 dark:hover:text-blue-400"
                                            >
                                              {part}
                                            </button>
                                          </React.Fragment>
                                        );
                                      })}
                                  </div>
                                )}
                              </div>

                              {getWorkspaceBrowser(artifact.step_id, artifact.artifact_key)
                                .loading ? (
                                <div className="flex items-center justify-center py-4">
                                  <Loader2 className="w-5 h-5 text-blue-500 animate-spin mr-2" />
                                  <span className="text-gray-600 dark:text-gray-400">
                                    Loading files...
                                  </span>
                                </div>
                              ) : getWorkspaceBrowser(
                                  artifact.step_id,
                                  artifact.artifact_key
                                ).error ? (
                                <div className="flex items-center p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
                                  <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 mr-2" />
                                  <span className="text-sm text-red-800 dark:text-red-300">
                                    {
                                      getWorkspaceBrowser(
                                        artifact.step_id,
                                        artifact.artifact_key
                                      ).error
                                    }
                                  </span>
                                </div>
                              ) : getSortedWorkspaceEntries(
                                  artifact.step_id,
                                  artifact.artifact_key
                                ).length === 0 ? (
                                <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
                                  No files in this directory
                                </div>
                              ) : (
                                <div className="overflow-x-auto">
                                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-100 dark:bg-gray-800">
                                      <tr>
                                        <th
                                          onClick={() =>
                                            toggleWorkspaceSort(
                                              artifact.step_id,
                                              artifact.artifact_key,
                                              "name"
                                            )
                                          }
                                          className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700"
                                        >
                                          Name
                                        </th>
                                        <th
                                          onClick={() =>
                                            toggleWorkspaceSort(
                                              artifact.step_id,
                                              artifact.artifact_key,
                                              "size"
                                            )
                                          }
                                          className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700"
                                        >
                                          Size
                                        </th>
                                        <th
                                          onClick={() =>
                                            toggleWorkspaceSort(
                                              artifact.step_id,
                                              artifact.artifact_key,
                                              "created"
                                            )
                                          }
                                          className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700"
                                        >
                                          Created
                                        </th>
                                        <th
                                          onClick={() =>
                                            toggleWorkspaceSort(
                                              artifact.step_id,
                                              artifact.artifact_key,
                                              "modified"
                                            )
                                          }
                                          className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700"
                                        >
                                          Modified
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                      {getSortedWorkspaceEntries(
                                        artifact.step_id,
                                        artifact.artifact_key
                                      ).map((entry, idx) => (
                                        <tr
                                          key={idx}
                                          onClick={() =>
                                            handleWorkspaceEntryClick(
                                              artifact.step_id,
                                              artifact.artifact_key,
                                              entry
                                            )
                                          }
                                          className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                                        >
                                          <td className="px-3 py-2 whitespace-nowrap text-sm">
                                            <div className="flex items-center">
                                              {entry.type === "directory" ? (
                                                <Folder className="w-4 h-4 text-yellow-500 dark:text-yellow-400 mr-2 flex-shrink-0" />
                                              ) : (
                                                <FileText className="w-4 h-4 text-blue-500 dark:text-blue-400 mr-2 flex-shrink-0" />
                                              )}
                                              <span className="text-gray-900 dark:text-white">
                                                {entry.name}
                                              </span>
                                            </div>
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                            {entry.type === "file"
                                              ? formatFileSize(entry.size ?? 0)
                                              : "-"}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                            {entry.created
                                              ? formatWorkspaceDate(entry.created)
                                              : "-"}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                            {entry.modified
                                              ? formatWorkspaceDate(entry.modified)
                                              : "-"}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
